/**
 *  webNotifications.js
 *  @description: Avisos del portal (api-go, migración 0005).
 *   - La bandeja: GET /web/notifications y marcar como leídos.
 *   - En vivo: GET /web/notifications/stream (Server-Sent Events). Se lee con
 *     fetch porque EventSource no permite mandar Authorization.
 *   - Notificaciones del navegador (Web Push): public/sw.js + la llave VAPID
 *     que da el servidor.
 */
import api, { token } from './apiV1';
import { URL_API } from './urlsApi';

// Evento de window con cada aviso que llega en vivo (detail = el aviso).
export const NOTIFICATION_EVENT = 'eleva:notification';

export const list = () => api.get('web/notifications');
export const markRead = (id) => api.post(`web/notifications/${id}/read`);
export const markAllRead = () => api.post('web/notifications/read');
export const sendTest = () => api.post('web/notifications/test');

// --- en vivo ---

/**
 * openStream llama a onEvent con cada evento ({kind: 'notification' |
 * 'read', ...}) y reconecta sola con espera creciente. Regresa la función
 * que la cierra. Se rinde si el servidor dice que no hay sesión (401) o que
 * la función no está disponible (404).
 */
export function openStream(onEvent, onOpen) {
   let closed = false;
   let controller = null;
   let wait = 2000;

   async function connect() {
      if (closed) return;
      const auth = token();
      if (!auth || !window.fetch || !window.ReadableStream || !window.TextDecoder) return;
      controller = window.AbortController ? new AbortController() : null;
      try {
         const res = await fetch(URL_API + 'api/v1/web/notifications/stream', {
            headers: { Authorization: 'Bearer ' + auth, Accept: 'text/event-stream' },
            signal: controller ? controller.signal : undefined,
            cache: 'no-store',
         });
         if (res.status === 401 || res.status === 404) return;
         if (!res.ok || !res.body) throw new Error('stream ' + res.status);
         wait = 2000;
         if (onOpen) onOpen();
         const reader = res.body.getReader();
         const decoder = new TextDecoder();
         let buffer = '';
         for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let cut;
            while ((cut = buffer.indexOf('\n\n')) >= 0) {
               const chunk = buffer.slice(0, cut);
               buffer = buffer.slice(cut + 2);
               const data = chunk
                  .split('\n')
                  .filter((l) => l.startsWith('data: '))
                  .map((l) => l.slice(6))
                  .join('\n');
               if (!data) continue;
               try {
                  onEvent(JSON.parse(data));
               } catch (e) {
                  // un evento mal formado no tumba el stream
               }
            }
         }
      } catch (e) {
         if (closed) return;
      }
      // El servidor cierra cada hora o se cayó la red: se reintenta.
      if (!closed) {
         setTimeout(connect, wait);
         wait = Math.min(wait * 2, 60000);
      }
   }

   connect();
   return () => {
      closed = true;
      if (controller) controller.abort();
   };
}

// --- notificaciones del navegador ---

export function pushSupported() {
   return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function pushPermission() {
   return pushSupported() ? Notification.permission : 'unsupported';
}

function urlBase64ToUint8Array(base64) {
   const padding = '='.repeat((4 - (base64.length % 4)) % 4);
   const raw = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
   const out = new Uint8Array(raw.length);
   for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
   return out;
}

/**
 * enablePush registra public/sw.js, se suscribe y manda la suscripción al
 * servidor. Con ask = false no muestra el aviso de permiso: solo resincroniza
 * si ya estaba concedido (se llama al abrir el portal). El permiso solo se
 * pide desde un clic: Safari lo ignora si no.
 * Regresa 'granted' | 'denied' | 'default' | 'unsupported' | 'unavailable'.
 */
export async function enablePush(ask) {
   if (!pushSupported()) return 'unsupported';
   let permission = Notification.permission;
   if (permission === 'default') {
      if (!ask) return 'default';
      permission = await Notification.requestPermission();
   }
   if (permission !== 'granted') return permission;

   const key = await api.get('web/push/public-key');
   if (!key.ok || !key.data || !key.data.publicKey) return 'unavailable';
   try {
      await navigator.serviceWorker.register('/sw.js');
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
         sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key.data.publicKey),
         });
      }
      const res = await api.post('web/push/subscription', sub.toJSON());
      return res.ok ? 'granted' : 'unavailable';
   } catch (e) {
      return 'unavailable';
   }
}

// disablePush da de baja este navegador (al cerrar sesión, para que el
// siguiente usuario de la computadora no reciba los avisos del anterior).
export async function disablePush() {
   if (!pushSupported()) return;
   try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg && (await reg.pushManager.getSubscription());
      if (!sub) return;
      await api.del('web/push/subscription', { endpoint: sub.endpoint });
      await sub.unsubscribe();
   } catch (e) {
      // sin red: el servidor la reasigna si otro usuario se suscribe aquí
   }
}

// --- mensajes por reporte (migración 0004) ---

export const messages = (requestId) => api.get(`requests/${requestId}/messages`);
export const messageSummary = () => api.get('requests/messages/summary');
export function sendMessage(requestId, text, image) {
   if (image) return api.upload('POST', `requests/${requestId}/messages`, [['image', image]], { text });
   return api.post(`requests/${requestId}/messages`, { text });
}
